import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Header } from './components/shared/Header';
import { ScrollToTop } from './components/shared/ScrollToTop';

// Lazy load components - moved to top level to avoid re-creation on every render
const AuthForm = React.lazy(() => import('./components/auth/AuthForm').then(m => ({ default: m.AuthForm })));
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const LandingPage = React.lazy(() => import('./components/landing/LandingPage').then(m => ({ default: m.LandingPage })));
const MarketingHomepage = React.lazy(() => import('./components/marketing/MarketingHomepage').then(m => ({ default: m.MarketingHomepage })));
const OnboardingFlow = React.lazy(() => import('./components/onboarding/OnboardingFlow').then(m => ({ default: m.OnboardingFlow })));
const SubscriptionSuccessPage = React.lazy(() => import('./components/landing/SubscriptionSuccessPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const ResetPasswordPage = React.lazy(() => import('./components/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const MarketplaceView = React.lazy(() => import('./components/marketplace/MarketplaceView').then(m => ({ default: m.MarketplaceView })));
const FeaturesPage = React.lazy(() => import('./components/marketingPages/FeaturesPage').then(m => ({ default: m.default })));
const PricingPage = React.lazy(() => import('./components/marketingPages/PricingPage').then(m => ({ default: m.default })));
const FeeCalculatorPage = React.lazy(() => import('./components/marketingPages/FeeCalculatorPage').then(m => ({ default: m.default })));
const IndustriesPage = React.lazy(() => import('./components/marketingPages/IndustriesPage').then(m => ({ default: m.default })));
const AboutPage = React.lazy(() => import('./components/marketingPages/AboutPage').then(m => ({ default: m.default })));
const ContactPage = React.lazy(() => import('./components/marketingPages/ContactPage').then(m => ({ default: m.default })));
const ResourcesPage = React.lazy(() => import('./components/marketingPages/ResourcesPage').then(m => ({ default: m.ResourcesPage })));
const TestimonialsPage = React.lazy(() => import('./components/marketingPages/TestimonialsPage').then(m => ({ default: m.default })));
const SecurityCompliancePage = React.lazy(() => import('./components/marketingPages/SecurityCompliancePage').then(m => ({ default: m.default })));
const TermsConditionsPage = React.lazy(() => import('./components/marketingPages/TermsConditionsPage').then(m => ({ default: m.default })));
const PrivacyPolicyPage = React.lazy(() => import('./components/marketingPages/PrivacyPolicyPage').then(m => ({ default: m.default })));
const SiteStatusPage = React.lazy(() => import('./components/marketingPages/SiteStatusPage').then(m => ({ default: m.default })));
const PlatformUpdatesPage = React.lazy(() => import('./components/marketingPages/PlatformUpdatesPage').then(m => ({ default: m.PlatformUpdatesPage })));
const Footer = React.lazy(() => import('./components/shared/Footer').then(m => ({ default: m.Footer })));
const UniversalSubscriberLoginModal = React.lazy(() => import('./components/customer/UniversalSubscriberLoginModal').then(m => ({ default: m.UniversalSubscriberLoginModal })));
const AdminAuthModal = React.lazy(() => import('./components/admin/AdminAuthModal').then(m => ({ default: m.AdminAuthModal })));
const AdminRedirect = React.lazy(() => import('./components/admin/AdminRedirect').then(m => ({ default: m.AdminRedirect })));

// Feature-specific pages
const LandingPageGeneratorPage = React.lazy(() => import('./components/marketingPages/features/LandingPageGeneratorPage').then(m => ({ default: m.default })));
const AutomatedPaymentsPage = React.lazy(() => import('./components/marketingPages/features/AutomatedPaymentsPage').then(m => ({ default: m.default })));
const DashboardAnalyticsPage = React.lazy(() => import('./components/marketingPages/features/DashboardAnalyticsPage').then(m => ({ default: m.default })));
const MarketingToolsPage = React.lazy(() => import('./components/marketingPages/features/MarketingToolsPage').then(m => ({ default: m.default })));

// Industry category pages
const HomePropertyPage = React.lazy(() => import('./components/marketingPages/industries/HomePropertyPage').then(m => ({ default: m.default })));
const PetCarePage = React.lazy(() => import('./components/marketingPages/industries/PetCarePage').then(m => ({ default: m.default })));
const FoodBeveragePage = React.lazy(() => import('./components/marketingPages/industries/FoodBeveragePage').then(m => ({ default: m.default })));
const HealthWellnessPage = React.lazy(() => import('./components/marketingPages/industries/HealthWellnessPage').then(m => ({ default: m.default })));
const EducationSkillsPage = React.lazy(() => import('./components/marketingPages/industries/EducationSkillsPage').then(m => ({ default: m.default })));
const AutomotiveTransportationPage = React.lazy(() => import('./components/marketingPages/industries/AutomotiveTransportationPage').then(m => ({ default: m.default })));
const HouseholdSuppliesPage = React.lazy(() => import('./components/marketingPages/industries/HouseholdSuppliesPage').then(m => ({ default: m.default })));
const TechnologyOfficePage = React.lazy(() => import('./components/marketingPages/industries/TechnologyOfficePage').then(m => ({ default: m.default })));
const PersonalLifestylePage = React.lazy(() => import('./components/marketingPages/industries/PersonalLifestylePage').then(m => ({ default: m.default })));
const KidsFamilyPage = React.lazy(() => import('./components/marketingPages/industries/KidsFamilyPage').then(m => ({ default: m.default })));
const HobbyRecreationPage = React.lazy(() => import('./components/marketingPages/industries/HobbyRecreationPage').then(m => ({ default: m.default })));
const SpecialtyNichePage = React.lazy(() => import('./components/marketingPages/industries/SpecialtyNichePage').then(m => ({ default: m.default })));

// Blog pages
const SubscriptionPricingGuidePage = React.lazy(() => import('./components/blog/SubscriptionPricingGuidePage').then(m => ({ default: m.default })));
const CustomerRetentionStrategiesPage = React.lazy(() => import('./components/blog/CustomerRetentionStrategiesPage').then(m => ({ default: m.default })));
const MarketingSubscriptionServicesPage = React.lazy(() => import('./components/blog/MarketingSubscriptionServicesPage').then(m => ({ default: m.default })));
const ScalingSubscriptionBusinessPage = React.lazy(() => import('./components/blog/ScalingSubscriptionBusinessPage').then(m => ({ default: m.default })));
const LegalConsiderationsPage = React.lazy(() => import('./components/blog/LegalConsiderationsPage').then(m => ({ default: m.default })));

function AdminRouteGuard({ user, isPlatformAdmin }: { user: any; isPlatformAdmin: boolean | null }) {
  const location = useLocation();
  const isAdminFromState = location.state?.isAdmin === true;

  console.log('[AdminRouteGuard]', {
    hasUser: !!user,
    isPlatformAdmin,
    isAdminFromState,
    locationState: location.state
  });

  if (!user) {
    console.log('[AdminRouteGuard] No user, redirecting to home');
    return <Navigate to="/" replace />;
  }

  if (isAdminFromState || isPlatformAdmin === true) {
    console.log('[AdminRouteGuard] Admin confirmed, loading AdminDashboard');
    return <React.Suspense fallback={
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto mb-4"></div>
          <p className="text-primary-700">Loading admin dashboard...</p>
        </div>
      </div>
    }>
      <AdminDashboard />
    </React.Suspense>;
  }

  if (isPlatformAdmin === false) {
    console.log('[AdminRouteGuard] Not an admin (isPlatformAdmin=false), redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('[AdminRouteGuard] Still loading admin status...');
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto mb-4"></div>
        <p className="text-primary-700">Loading admin access...</p>
      </div>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);
  const [showSubscriberLogin, setShowSubscriberLogin] = useState(false);
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  // Disable automatic scroll restoration
  React.useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const handleSignUpClick = () => {
    // Navigate directly to onboarding flow
    window.location.href = '/onboarding';
  };

  const handleSignInClick = () => {
    setShowAuth(true);
    setAuthMode('signin');
    setShowOnboarding(false);
  };

  const handleSubscriberLoginClick = () => {
    setShowAuth(false);
    setShowSubscriberLogin(true);
  };

  const handleBackToHome = () => {
    setShowAuth(false);
    setShowOnboarding(false);
  };

  const handleAuthToggle = () => {
    setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
  };

  const handleAuthSuccess = async (isNewUser: boolean) => {
    setShowAuth(false);
    setEmailNotVerified(false);
    setUnverifiedEmail('');

    if (isNewUser) {
      setShowOnboarding(true);
      setNeedsOnboarding(true);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };


  // Check if email needs verification
  React.useEffect(() => {
    const checkEmailVerification = async () => {
      if (!user) {
        setEmailNotVerified(false);
        setUnverifiedEmail('');
        return;
      }

      const { data: { user: currentUser } } = await (await import('./lib/supabase')).supabase.auth.getUser();

      if (currentUser && !currentUser.email_confirmed_at) {
        console.log('[App] User email not verified, showing auth form');
        setEmailNotVerified(true);
        setUnverifiedEmail(currentUser.email || '');
        setShowAuth(true);
        setAuthMode('signup');
      } else {
        setEmailNotVerified(false);
        setUnverifiedEmail('');
      }
    };

    checkEmailVerification();
  }, [user]);

  // Check onboarding status and admin status when user logs in
  React.useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) {
        setNeedsOnboarding(null);
        setIsPlatformAdmin(null);
        return;
      }

      try {
        const { data: profile, error } = await (await import('./lib/supabase')).supabase
          .from('user_profiles')
          .select('needs_onboarding_completion, onboarding_completed, current_onboarding_step, is_platform_admin')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching profile:', error);
          setNeedsOnboarding(false);
          setIsPlatformAdmin(false);
          return;
        }

        // User needs onboarding if they have an incomplete onboarding OR if they have a saved step in progress
        const hasIncompleteOnboarding = profile?.needs_onboarding_completion && !profile?.onboarding_completed;
        const hasOnboardingInProgress = profile?.current_onboarding_step && profile?.current_onboarding_step > 0;

        if (hasIncompleteOnboarding || hasOnboardingInProgress) {
          setNeedsOnboarding(true);
        } else {
          setNeedsOnboarding(false);
        }

        setIsPlatformAdmin(profile?.is_platform_admin || false);
      } catch (err) {
        console.error('Error checking user status:', err);
        setNeedsOnboarding(false);
        setIsPlatformAdmin(false);
      }
    };

    checkUserStatus();
  }, [user]);

  // Show loading spinner while checking initial auth
  if (loading || (user && (needsOnboarding === null || isPlatformAdmin === null))) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto mb-4"></div>
          <p className="text-primary-700">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <React.Suspense fallback={
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto mb-4"></div>
          <p className="text-primary-700">Loading...</p>
        </div>
      </div>
    }>
      {showSubscriberLogin && (
        <UniversalSubscriberLoginModal onClose={() => setShowSubscriberLogin(false)} />
      )}
      <Router>
        <ScrollToTop />
        {showAdminAuth && (
          <AdminAuthModal onClose={() => setShowAdminAuth(false)} />
        )}
        <Routes>
          {/* Onboarding Route */}
          <Route
            path="/onboarding"
            element={
              emailNotVerified ? (
                <Navigate to="/" replace />
              ) : (
                <OnboardingFlow onComplete={handleOnboardingComplete} />
              )
            }
          />
          
          {/* Public landing pages for subscriptions */}
          <Route path="/landing/:customUrl" element={<LandingPage />} />
          <Route path="/s/:customUrl" element={<LandingPage />} />

          {/* Subscription success/congratulations page */}
          <Route path="/subscription-success/:customUrl/:subscriberId" element={<SubscriptionSuccessPage />} />

          {/* Password Reset Page */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Customer portal for managing subscriptions - redirects to subscription success page */}
          <Route path="/customer/:customUrl/:subscriberId" element={<SubscriptionSuccessPage />} />
          
          {/* Features Page */}
          <Route
            path="/features/subplz-page-generator"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <LandingPageGeneratorPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/features/automated-payments" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <AutomatedPaymentsPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/features/dashboard-analytics" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <DashboardAnalyticsPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/features/marketing-tools" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <MarketingToolsPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/features" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <FeaturesPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          {/* Pricing Page */}
          <Route
            path="/pricing/fee-calculator"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <FeeCalculatorPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          <Route
            path="/pricing/testimonials"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <TestimonialsPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          <Route
            path="/pricing"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <PricingPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />
          
          {/* Industries Page */}
          <Route 
            path="/industries" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <IndustriesPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          {/* Individual Industry Category Pages */}
          <Route 
            path="/industries/home-property" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <HomePropertyPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/pet-care" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <PetCarePage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/food-beverage" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <FoodBeveragePage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/health-wellness" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <HealthWellnessPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/education-skills" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <EducationSkillsPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/automotive-transportation" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <AutomotiveTransportationPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/household-supplies" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <HouseholdSuppliesPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/technology-office" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <TechnologyOfficePage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/personal-lifestyle" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <PersonalLifestylePage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/kids-family" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <KidsFamilyPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/hobby-recreation" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <HobbyRecreationPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          <Route 
            path="/industries/specialty-niche" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <SpecialtyNichePage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          {/* Resources Page */}
          <Route
            path="/resources"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <ResourcesPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Blog Pages */}
          <Route
            path="/blog/subscription-pricing-guide"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <SubscriptionPricingGuidePage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          <Route
            path="/blog/customer-retention-strategies"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <CustomerRetentionStrategiesPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          <Route
            path="/blog/marketing-subscription-services"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <MarketingSubscriptionServicesPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          <Route
            path="/blog/scaling-subscription-business"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <ScalingSubscriptionBusinessPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          <Route
            path="/blog/legal-considerations"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <LegalConsiderationsPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* About Page */}
          <Route
            path="/about"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <AboutPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Security & Compliance Page */}
          <Route
            path="/security"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <SecurityCompliancePage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Terms & Conditions Page */}
          <Route
            path="/terms"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <TermsConditionsPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Privacy Policy Page */}
          <Route
            path="/privacy"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <PrivacyPolicyPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Site Status Page */}
          <Route
            path="/status"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <SiteStatusPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Platform Updates / Changelog Page */}
          <Route
            path="/changelog"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <PlatformUpdatesPage />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />

          {/* Contact Page */}
          <Route 
            path="/contact" 
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <ContactPage onSignUpClick={handleSignUpClick} />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            } 
          />
          
          {/* Marketplace Route */}
          <Route
            path="/marketplace"
            element={
              <>
                <Header
                  onSignUpClick={handleSignUpClick}
                  onSignInClick={handleSignInClick}
                  user={user}
                />
                <MarketplaceView />
                <Footer onAdminClick={() => setShowAdminAuth(true)} />
              </>
            }
          />
          
          {/* Dashboard route - only for authenticated users */}
          <Route
            path="/dashboard"
            element={
              user ? (
                emailNotVerified ? (
                  <Navigate to="/" replace />
                ) : (
                  <Dashboard />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Admin redirect route - handles admin login redirects */}
          <Route
            path="/admin-redirect"
            element={<AdminRedirect />}
          />

          {/* Admin Dashboard route - only for platform admins */}
          <Route
            path="/admin"
            element={<AdminRouteGuard user={user} isPlatformAdmin={isPlatformAdmin} />}
          />
          
          {/* Home route */}
          <Route
            path="/"
            element={
              (() => {
                const hasOnboardingStep = localStorage.getItem('onboarding_current_step');

                // If user's email is not verified, show auth form with verification modal
                if (user && emailNotVerified) {
                  return (
                    <AuthForm
                      mode={authMode}
                      onToggleMode={handleAuthToggle}
                      onBackToHome={handleBackToHome}
                      onAuthSuccess={handleAuthSuccess}
                      onSubscriberLoginClick={handleSubscriberLoginClick}
                      showVerification={true}
                      verificationEmailAddress={unverifiedEmail}
                    />
                  );
                }

                if (user && showOnboarding) {
                  return <Navigate to="/onboarding?source=dashboard" replace />;
                }

                if (user && needsOnboarding) {
                  return <Navigate to="/onboarding?source=dashboard" replace />;
                }

                if (user && hasOnboardingStep && needsOnboarding !== false) {
                  return <Navigate to="/onboarding?source=dashboard" replace />;
                }

                if (showOnboarding && !user) {
                  return <Navigate to="/onboarding" replace />;
                }

                return user ? (
                  isPlatformAdmin ? (
                    <Navigate to="/admin" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : showAuth ? (
                  <AuthForm
                    mode={authMode}
                    onToggleMode={handleAuthToggle}
                    onBackToHome={handleBackToHome}
                    onAuthSuccess={handleAuthSuccess}
                    onSubscriberLoginClick={handleSubscriberLoginClick}
                  />
                ) : (
                  <>
                    <Header
                      onSignUpClick={handleSignUpClick}
                      onSignInClick={handleSignInClick}
                      user={user}
                    />
                    <MarketingHomepage onSignUpClick={handleSignUpClick} />
                    <Footer onAdminClick={() => setShowAdminAuth(true)} />
                  </>
                );
              })()
            }
          />
          
          {/* Direct subscription URL (e.g., /my-subscription-name) */}
          <Route path="/:customUrl" element={<LandingPage />} />

          {/* Catch all route - should be last */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </React.Suspense>
  );
}

export default App;